/*
 * Copyright © 2024 Himitsu Lab Limited. All Rights Reserved.
 */

import { GridLayoutProps, TrackLoop } from '@livekit/components-react'
import * as React from 'react'
import {
  useControlBarAccessHooks,
  useGetHostAndCoHost,
  useHostCoHostAvailabilityHook,
  useMeetingStartTimerHook,
} from '../../livekitHooks'
// import { Track } from 'livekit-client'

import { LiveShoppingChat } from './LiveShoppingChat'
import { useCustomRoomContext } from '../CustomRoomContext'
import { useTranslation } from 'react-i18next'

/**
 * CustomLiveShoppingLayout component displays the nested participants in a grid where every participant has the same size.
 *
 * @param {GridLayoutProps} props - The props object containing tracks and additional props.
 * @return {JSX.Element} The rendered GridLayout component.
 */

export function CustomLiveShoppingLayout({ tracks, ...props }: GridLayoutProps) {
  const { hostAndCoHosts } = useGetHostAndCoHost()
  const { saveClicked, setSaveClicked } = useCustomRoomContext()
  const { isHostOrCohostAvailable } = useHostCoHostAvailabilityHook()
  const { isHostOrCoHostAvailableRoom } = useControlBarAccessHooks()
  const { formattedStartTime, isShowStarted, countdown } = useMeetingStartTimerHook()
  const { t } = useTranslation()

  const hostCohostTracks = React.useMemo(() => {
    return tracks.filter((track) => hostAndCoHosts.includes(track.participant.sid))
  }, [tracks, hostAndCoHosts])

  // const cameraEnabledTrack = React.useMemo(() => {
  //   const cameraEnabledTracks = hostCohostTracks
  //     .filter((track) => track.source === Track.Source.Camera)
  //     .map((track) => ({
  //       trackSid: track.publication?.trackSid ?? track.participant.sid,
  //       participantSid: track.participant.sid,
  //       source: track.source,
  //       participant: track.participant,
  //     }))
  //   if (cameraEnabledTracks.length > 0) {
  //     return cameraEnabledTracks[0]
  //   } else {
  //     return undefined
  //   }
  // }, [hostCohostTracks])

  // const screenSharedTrack = React.useMemo(() => {
  //   const screenSharedTracks = hostCohostTracks
  //     .filter((track) => track.source === Track.Source.ScreenShare)
  //     .map((track) => ({
  //       trackSid: track.publication?.trackSid ?? track.participant.sid,
  //       participantSid: track.participant.sid,
  //       source: track.source,
  //       participant: track.participant,
  //     }))
  //   if (screenSharedTracks.length > 0) {
  //     return screenSharedTracks[0]
  //   } else {
  //     return undefined
  //   }
  // }, [hostCohostTracks])

  return (
    <div className="flex flex-col lg:flex-row gap-5 h-full w-auto overflow-y-auto scrollbar-hide">
      <div className="lg:w-[60vw] w-full h-full border-2 rounded-lg">
        <TrackLoop tracks={hostCohostTracks}>{props.children}</TrackLoop>
      </div>

      <div className="lg:w-[40vw] w-full h-full flex flex-col">
        {!isHostOrCoHostAvailableRoom && (
          <div className="mb-4 space-y-1">
            {!isHostOrCohostAvailable ? (
              <>
                {!isShowStarted && (
                  <p className="text-lg text-center">
                    {countdown ? `Show starts in ${countdown}` : `Show starts at ${formattedStartTime}`}
                  </p>
                )}

                <p className="text-lg font-semibold text-center">{t('waitForHost')}</p>
                {/* <div className="flex justify-center mt-2">
                  <button
                    className="bg-amber-400 text-black px-4 py-1 rounded-lg"
                    onClick={() => {
                      setShareClicked(!shareClicked)
                    }}
                  >
                    {t('shareShow')}
                  </button>
                </div> */}
              </>
            ) : (
              <>
                {!isShowStarted && (
                  <>
                    <p className="text-lg text-center">{countdown ? `Show starts in` : `Show starts at`}</p>
                    <p className="text-lg font-semibold text-center">{countdown || formattedStartTime}</p>{' '}
                  </>
                )}
                <div className="flex gap-3 mt-2 justify-center">
                  {!isShowStarted && !isHostOrCoHostAvailableRoom && (
                    <button
                      className="bg-amber-400 text-black px-4 py-1 rounded-lg"
                      onClick={() => {
                        setSaveClicked(!saveClicked)
                      }}
                    >
                      {t('save&NotifyMe')}
                    </button>
                  )}

                  {/* <button
                    className="bg-amber-400 text-black px-4 py-1 rounded-lg"
                    onClick={() => {
                      setShareClicked(!shareClicked)
                    }}
                  >
                    {t('shareShow')}
                  </button> */}
                </div>
              </>
            )}
          </div>
        )}
        <div className="flex-1 h-full overflow-hidden">
          <LiveShoppingChat />
        </div>
      </div>
    </div>
  )
}
